import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import type {
  AccountRow,
  OpportunityRow,
  OpportunityPersona,
  OpportunityActivityCall,
  OpportunityActivityEmail,
} from '@shared/flow/account-view';
import { DetailKV } from '../FlowDetail';
import { parseDomainLink, sfdcRecordUrl, SfdcCloudIcon, GongCallIcon } from '../links';
import { accountInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { sanitizeRichHtml } from '../cells';
import { joinNonBlank } from '@shared/flow/non-blank';
import { useFlowAccountOpportunities, useFlowAccountOpportunityActivity } from './hooks';
import { OPPORTUNITY_TILE_CONFIG, CALL_TILE_CONFIG, EMAIL_TILE_CONFIG } from './AccountTimeline';
import { fmtDate as fmt, money, num, yesNo, resolvedLookalikes } from './accountFieldFormat';
import {
  PERSONA_ROWS,
  personaSetCount,
  primaryClayUserDisplay,
  oppCloseStatus,
  OPP_STATUS_LABEL,
  stageConveysCloseStatus,
  oppMetaText,
  metaChipLabel,
  gongCallUrl,
  callParticipantsText,
  DEAL_WINDOW_NOTE,
  buildOpportunityActivityStream,
  type OppCloseStatus,
} from './opportunityPresentation';

// Re-exported so `AccountDetailPanels.test.tsx:21`'s
// `import { buildOpportunityActivityStream } from '../AccountDetailPanels'`
// keeps working unmodified (plan 005 §0.9) — the real implementation now
// lives in `opportunityPresentation.ts`.
export { buildOpportunityActivityStream };

/** Panels for the drill-down's quads: Fields card, Other-fields list, Opps. */

const SECTION_HEAD = 'text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium mb-1';
const LONGTEXT_HEAD = 'text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium mt-3 mb-1';

/** A titled group of DetailKV rows (one block of the two-column Fields layout). */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    // break-inside-avoid keeps a section from splitting across CSS columns.
    <div className="break-inside-avoid mb-4">
      <div className={SECTION_HEAD}>{title}</div>
      {children}
    </div>
  );
}

/**
 * The full synced field surface, organized into relevance sections laid out in
 * a two-column grid (top-left quad, Fields tab). Long-text blocks span the full
 * width below the grid, rendered only when present.
 */
export function AccountFieldsCard({ account, twoColumn = true, hierarchy }: { account: AccountRow; twoColumn?: boolean; hierarchy?: React.ReactNode }) {
  // email_domain_link is an SFDC HYPERLINK() formula (an <a> string) — parse it
  // to a clean hostname + safe href instead of rendering the raw markup.
  const domain = parseDomainLink(account.email_domain_link);

  // Customer Lookalikes (U5a Decision 7 / U6, R5): slot N is null-name
  // whenever the target account is absent from the mirror (dangling `_id`)
  // — skip those rather than rendering a bare id (per the schema's own doc
  // comment on `account_lookalike_1_id`). Section hides entirely below when
  // no slot resolves to a name (fill rate ~22%, plan Approach note).
  const lookalikes = resolvedLookalikes(account);

  return (
    <div>
      {/* CSS columns (masonry) — sections pack tightly instead of stretching to the
          tallest grid-row neighbor (which left a gap under short sections). */}
      {/* The min-[900px] variant is a VIEWPORT query, not a container query — a
          narrow embed (contact detail's Account Info panel) must force 1 column
          or the sections squeeze into ~150px and truncate. */}
      <div className={twoColumn ? 'columns-1 min-[900px]:columns-2 gap-6' : 'columns-1'}>
        <Section title="Company">
          <DetailKV k="Type" v={account.account_type} />
          <DetailKV k="Industry" v={account.industry} />
          <DetailKV k="Employees" v={num(account.number_of_employees)} />
          <DetailKV k="Segment" v={account.company_segment} />
          <DetailKV k="HQ" v={[account.billing_city, account.billing_state, account.billing_country].filter(Boolean).join(', ') || null} />
          <DetailKV k="Domain" v={domain?.label} href={domain?.href} />
          <DetailKV k="Sales Tier" v={account.sales_tier} />
          <DetailKV k="Account Fit" v={account.account_fit} />
          <DetailKV k="F500 Rank" v={num(account.fortune_500_rank)} />
        </Section>
        {/* Parent/child account links (the drilldown passes them; contact-detail embeds do not). */}
        {hierarchy}
        <Section title="Pipeline & Commercial">
          <DetailKV k="PCV" v={money(account.pcv)} />
          <DetailKV k="ARR" v={money(account.arr)} />
          <DetailKV k="ARR Opportunity" v={money(account.arr_opportunity)} />
          <DetailKV k="Open Pipeline" v={money(account.open_pipeline)} />
          <DetailKV k="# Open Opps" v={num(account.number_of_open_opp)} />
          <DetailKV k="# Won Opps" v={num(account.number_of_won_opp)} />
          <DetailKV k="# Open Renewals" v={num(account.number_of_open_renewals)} />
          <DetailKV k="# New-Biz Opps" v={num(account.number_of_open_new_biz_opps)} />
          <DetailKV k="Pipeline Stage" v={account.last_opportunity_stage} />
          <DetailKV k="Product" v={account.product_tier} />
          <DetailKV k="Plan" v={account.plan_interval} />
        </Section>
        <Section title="Rep Coverage">
          <DetailKV k="GTME Owner" v={account.owner_name} />
          <DetailKV
            k="GTME Meeting"
            v={account.owner_meeting_link?.replace(/^https?:\/\//, '')}
            href={account.owner_meeting_link ?? undefined}
          />
          <DetailKV
            k="GTME Cust. Mtg"
            v={account.owner_customer_meeting_link?.replace(/^https?:\/\//, '')}
            href={account.owner_customer_meeting_link ?? undefined}
          />
          <DetailKV k="ClayDR" v={account.claydr_name} />
          <DetailKV
            k="CDR Meeting"
            v={account.claydr_meeting_link?.replace(/^https?:\/\//, '')}
            href={account.claydr_meeting_link ?? undefined}
          />
          <DetailKV
            k="CDR Cust. Mtg"
            v={account.claydr_customer_meeting_link?.replace(/^https?:\/\//, '')}
            href={account.claydr_customer_meeting_link ?? undefined}
          />
          <DetailKV k="GS" v={account.gs_name} />
        </Section>
        <Section title="Signals & Engagement">
          <DetailKV k="Last Signal" v={account.lim_name} href={account.lim_link ?? undefined} />
          <DetailKV k="Signal Date" v={fmt(account.lim_date)} />
          <DetailKV k="Signal Type" v={joinNonBlank([account.lim_type, account.lim_subtype])} />
          <DetailKV k="L90 Signals" v={num(account.l90_signal_count)} />
          <DetailKV k="L90 Web Visits" v={num(account.l90_website_visits)} />
          <DetailKV k="L90 Clay Users" v={num(account.l90_active_clay_users)} />
          <DetailKV k="Engagement (L90)" v={num(account.engagement_score_90_days)} />
          <DetailKV k="Eng. Status" v={account.engagement_status_90_days} />
          <DetailKV k="Eng. Channels" v={account.engagement_channels} />
          <DetailKV k="# Workspaces" v={num(account.number_of_workspace)} />
          <DetailKV k="WS Created L90" v={num(account.workspaces_created_last_90_days)} />
          <DetailKV k="Suppressed" v={yesNo(account.suppressed)} />
        </Section>
        {/* Workspace (U6, R4) — the parsed primary-workspace link (Decision
            4). Hidden entirely when the account has no primary workspace
            (fill rate ~78%, plan Approach note) — matches how this card
            treats every other empty field/section. */}
        {account.account_primary_workspace && (
          <Section title="Workspace">
            <DetailKV
              k="Primary Workspace"
              v={account.account_primary_workspace.url.replace(/^https?:\/\//, '')}
              href={account.account_primary_workspace.url}
            />
            <DetailKV k="Workspace Tier" v={account.product_tier} />
            <DetailKV k="Plan Interval" v={account.plan_interval} />
          </Section>
        )}
        {/* Customer Lookalikes (U6, R5) — hidden entirely when no slot
            resolves to a name (fill rate ~22%, plan Approach note). */}
        {lookalikes.length > 0 && (
          <Section title="Customer Lookalikes">
            {lookalikes.map(l => (
              <div key={l.id} className="mb-2 last:mb-0">
                <Link
                  to={accountInCommandCenter(l.id)}
                  className="text-[12px] font-medium text-content-action dark:text-blueberry-400 hover:underline"
                >
                  {l.name}
                </Link>
                {l.useCase && (
                  <div className="text-[11px] leading-snug text-content-secondary dark:text-nightshade-400 mt-0.5">{l.useCase}</div>
                )}
              </div>
            ))}
            {account.account_lookalike_last_enriched && (
              <div className="text-[10.5px] text-content-tertiary dark:text-nightshade-500 mt-1">
                Last enrichment: {fmt(account.account_lookalike_last_enriched)}
              </div>
            )}
          </Section>
        )}
      </div>
      {account.engagement_summary_90_days && (
        <>
          <div className={LONGTEXT_HEAD}>Engagement Summary</div>
          {/* Rich HTML from SFDC (Engagement_Summary_90_days__c) — a <ul> of links.
              Render via the project's read-only pattern: DOMPurify.sanitize into a
              prose block (matches DisplayValue / DraftPreviewPane). A <div> (not <p>)
              since the content is a list — a <ul> inside a <p> is invalid HTML.
              GTMOPS-2136: every anchor is forced to open in a new tab (see
              sanitizeRichHtml in ../cells.tsx). */}
          <div
            className="prose prose-sm max-w-none text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-400"
            dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(account.engagement_summary_90_days) }}
          />
        </>
      )}
      {account.fit_score_summary && (
        <>
          <div className={LONGTEXT_HEAD}>Fit Score Summary</div>
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-400">{account.fit_score_summary}</p>
        </>
      )}
      {account.tech_stack && (
        <>
          <div className={LONGTEXT_HEAD}>Tech Stack (first-party)</div>
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-400">{account.tech_stack}</p>
        </>
      )}
      {account.technologies_used && (
        <>
          <div className={LONGTEXT_HEAD}>Tech Stack (scraped)</div>
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-400">{account.technologies_used}</p>
        </>
      )}
      {account.lim_description && (
        <>
          <div className={LONGTEXT_HEAD}>Last Signal Description</div>
          <p className="text-[12px] leading-relaxed text-content-secondary dark:text-nightshade-400">{account.lim_description}</p>
        </>
      )}
    </div>
  );
}

/**
 * Opportunities panel (top-right quad, Opps tab) — Bucket B U8, rebuilt to
 * match the prototype's rich expand (docs/flow/command-ux-bucket-b-
 * prototype.html :927-1052) with the plan's decided cuts: no Notes section,
 * no call duration/direction/AI-summary, no email direction badge (Decisions
 * 5/8/9), Key Personas as a fixed 6-row section (Decision 7), and Next Step
 * History + Calls + Emails merged into ONE time-interleaved stream capped at
 * 10 items with a "View more in Salesforce ↗" overflow link (Decision 7).
 */

const CHIP_BASE = 'text-[10px] rounded px-1.5 py-px whitespace-nowrap';
const CHIP_FILLED = `${CHIP_BASE} bg-bg-secondary dark:bg-nightshade-800 text-content-tertiary dark:text-nightshade-500`;
// `.mchip.empty` (prototype :235-236) — dashed border, no fill.
const CHIP_EMPTY = `${CHIP_BASE} border border-dashed border-secondary dark:border-nightshade-700 text-content-faint dark:text-nightshade-600`;

function MetaChip({ count, singular, plural, emptyLabel }: { count: number; singular: string; plural: string; emptyLabel: string }) {
  return (
    <span className={count ? CHIP_FILLED : CHIP_EMPTY}>
      {metaChipLabel({ count, singular, plural, emptyLabel })}
    </span>
  );
}

const PERSONA_LABEL_CLASS = 'w-[150px] shrink-0 text-content-tertiary dark:text-nightshade-500';
const PERSONA_NOTSET_CLASS = 'italic text-content-faint dark:text-nightshade-600';

/** One Key-Personas row: a resolved-name SFDC contact link, or an italic
 * "— not set" empty (prototype :965-971). Primary Clay User is special-cased
 * to the denormalized `primary_clay_user_name`/`_title` display fields
 * (Approach note, U8) rather than the resolved-contact link every other role
 * uses — those two fields are already on the Opportunity object (no join),
 * so they're the more current display source for that one role. */
function PersonaRow({
  label, persona, isPrimaryClayUser, primaryClayUserName, primaryClayUserTitle,
}: {
  label: string;
  persona: OpportunityPersona;
  isPrimaryClayUser: boolean;
  primaryClayUserName: string | null;
  primaryClayUserTitle: string | null;
}) {
  if (isPrimaryClayUser) {
    const display = primaryClayUserDisplay(primaryClayUserName, primaryClayUserTitle);
    return (
      <div className="flex items-baseline gap-2 py-0.5 text-[11.5px]">
        <span className={PERSONA_LABEL_CLASS}>{label}</span>
        {display
          ? <span className="min-w-0 truncate text-content-primary dark:text-nightshade-100">{display}</span>
          : <span className={PERSONA_NOTSET_CLASS}>— not set</span>}
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-2 py-0.5 text-[11.5px]">
      <span className={PERSONA_LABEL_CLASS}>{label}</span>
      {persona
        ? (
          <a
            href={sfdcRecordUrl('Contact', persona.contact_id)} target="_blank" rel="noreferrer"
            className="min-w-0 truncate text-content-action dark:text-blueberry-400 hover:underline"
          >
            {persona.name ?? persona.contact_id} ↗
          </a>
        )
        : <span className={PERSONA_NOTSET_CLASS}>— not set</span>}
    </div>
  );
}

const SUB_ROW_CLASS = 'border-t border-primary/30 dark:border-nightshade-800 first:border-t-0';
const SUB_TRIG_CLASS = 'w-full flex items-center gap-2 py-1 text-[11.5px] text-left hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded';
const SUB_CHEVRON_CLASS = 'w-3 h-3 shrink-0 text-content-tertiary dark:text-nightshade-500';
const SUB_TILE_CLASS = 'w-4 h-4 shrink-0 rounded flex items-center justify-center';
const SUB_TILE_ICON_CLASS = 'w-2.5 h-2.5';
const SUB_DATE_CLASS = 'w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500';
const SUB_TITLE_CLASS = 'min-w-0 flex-1 truncate font-medium text-content-primary dark:text-nightshade-100';
const SUB_DETAIL_CLASS = 'pl-7 pb-2 space-y-1';
const SUB_LINK_CLASS = 'inline-flex items-center gap-1 text-[11px] text-content-action dark:text-blueberry-400 hover:underline';

/**
 * A sub-row's collapsed trigger is a `role="button"` DIV, not a native
 * `<button>` — the collapsed header now carries a quick out-link `<a>`
 * (Gong for calls, SFDC cloud for emails), and nesting interactive `<a>`
 * content inside a real `<button>` is invalid HTML (same reasoning as
 * `OppRow`'s and `AccountTimeline`'s row triggers, which use this same
 * div+role pattern for exactly this reason).
 */
function subRowKeyDown(onToggle: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
  };
}

/** Expandable Call sub-row (`.sub-row`, prototype :982-1004) — title/date
 * collapsed; expand → participants + "Open in Gong ↗". No duration, no
 * direction, no AI summary (Decision 8 — the account-level `gong_attendance`
 * mirror carries none of those, and per-opp attribution doesn't exist).
 *
 * Collapsed-row discoverability follow-up: a Phone icon tile (distinguishes
 * calls from emails at a glance, matching the Timeline/Signals/Opps icon-tile
 * convention — `CALL_TILE_CONFIG`) and a quick Gong out-link icon (so the
 * Gong deep-link is visible without expanding, same "no unfurl needed"
 * treatment the other collapsed rows already got). */
function CallSubRow({ call, isOpen, onToggle }: { call: OpportunityActivityCall; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className={SUB_ROW_CLASS}>
      <div
        role="button" tabIndex={0} aria-expanded={isOpen} onClick={onToggle} onKeyDown={subRowKeyDown(onToggle)}
        className={SUB_TRIG_CLASS}
      >
        {isOpen ? <ChevronDown className={SUB_CHEVRON_CLASS} /> : <ChevronRight className={SUB_CHEVRON_CLASS} />}
        <span className={`${SUB_TILE_CLASS} ${CALL_TILE_CONFIG.tileClass}`}>
          <CALL_TILE_CONFIG.Icon className={SUB_TILE_ICON_CLASS} />
        </span>
        <span className={SUB_DATE_CLASS}>{fmt(call.started_at)}</span>
        <span className={SUB_TITLE_CLASS}>{call.call_title}</span>
        <GongCallIcon url={gongCallUrl(call.call_id)} />
      </div>
      {isOpen && (
        <div className={SUB_DETAIL_CLASS}>
          <div className="text-[11px] text-content-secondary dark:text-nightshade-400">
            {callParticipantsText(call.participants)}
          </div>
          <a
            href={gongCallUrl(call.call_id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
            className={SUB_LINK_CLASS}
          >
            Open in Gong ↗
          </a>
        </div>
      )}
    </div>
  );
}

/** Expandable Email sub-row (prototype :1006-1024, minus the `dirbadge` —
 * Decision 9) — date + subject collapsed; expand → body + owner/contact +
 * "Open in Salesforce" Task link.
 *
 * Collapsed-row discoverability follow-up: a Mail icon tile (`EMAIL_TILE_CONFIG`)
 * distinguishes it from `CallSubRow` at a glance, plus the same `SfdcCloudIcon`
 * quick out-link every other collapsed row in this surface already has. */
function EmailSubRow({ email, isOpen, onToggle }: { email: OpportunityActivityEmail; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className={SUB_ROW_CLASS}>
      <div
        role="button" tabIndex={0} aria-expanded={isOpen} onClick={onToggle} onKeyDown={subRowKeyDown(onToggle)}
        className={SUB_TRIG_CLASS}
      >
        {isOpen ? <ChevronDown className={SUB_CHEVRON_CLASS} /> : <ChevronRight className={SUB_CHEVRON_CLASS} />}
        <span className={`${SUB_TILE_CLASS} ${EMAIL_TILE_CONFIG.tileClass}`}>
          <EMAIL_TILE_CONFIG.Icon className={SUB_TILE_ICON_CLASS} />
        </span>
        <span className={SUB_DATE_CLASS}>{fmt(email.effective_at)}</span>
        <span className={SUB_TITLE_CLASS}>{email.subject ?? '(no subject)'}</span>
        <SfdcCloudIcon kind="Task" id={email.source_id} />
      </div>
      {isOpen && (
        <div className={SUB_DETAIL_CLASS}>
          {email.description && (
            <p className="text-[11px] leading-relaxed text-content-secondary dark:text-nightshade-400">{email.description}</p>
          )}
          {(email.owner_name || email.contact_name) && (
            <div className="text-[10.5px] text-content-tertiary dark:text-nightshade-500">
              {[email.owner_name, email.contact_name].filter(Boolean).join(' · ')}
            </div>
          )}
          <a
            href={sfdcRecordUrl('Task', email.source_id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
            className={SUB_LINK_CLASS}
          >
            Open in Salesforce ↗
          </a>
        </div>
      )}
    </div>
  );
}

/** Non-expandable Next Step History entry (`.nsh-item`, prototype :243-247,
 * :974-975) — no chevron, no sub-detail; NSH is already free text, there's
 * nothing further to expand into. */
function NshStreamRow({ when, text }: { when: string | null; text: string }) {
  return (
    <div className="border-t border-primary/30 dark:border-nightshade-800 first:border-t-0 py-1.5 pl-5 pr-1 text-[11.5px]">
      <span className="font-mono text-[10.5px] text-content-tertiary dark:text-nightshade-500">{when ?? 'Undated'}</span>
      {' — '}
      <span className="whitespace-pre-line text-content-secondary dark:text-nightshade-300">{text}</span>
    </div>
  );
}

const OSEC_HEAD_CLASS = 'text-[10.5px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500 font-medium';
const OSEC_COUNT_CLASS = 'font-mono text-[10px] text-content-faint dark:text-nightshade-600 ml-1';

/** Tone classes reused by both the stage pill (when the stage text itself
 * is won/lost) and the separate status pill (when it isn't) — same green/
 * red semantic pattern as RequestStatusBadge's STATUS_STYLES (`complete`/
 * `failed`, EnrollQueue/request-format.tsx), but one shade stronger
 * (`bg-green`/`bg-red` = matcha-100/pomegranate-100, not the `-light` 50-
 * shades) plus a matching border — Spencer's "pills aren't bold enough"
 * feedback on the first pass. */
const OPP_STATUS_TONE: Record<OppCloseStatus, string> = {
  open: 'bg-green dark:bg-matcha-900/50 border-matcha-200 dark:border-matcha-700 text-content-success dark:text-emerald-400',
  won: 'bg-green dark:bg-matcha-900/50 border-matcha-200 dark:border-matcha-700 text-content-success dark:text-emerald-400',
  lost: 'bg-red dark:bg-pomegranate-900/50 border-pomegranate-200 dark:border-pomegranate-700 text-content-danger dark:text-pomegranate-400',
  closed: 'bg-red dark:bg-pomegranate-900/50 border-pomegranate-200 dark:border-pomegranate-700 text-content-danger dark:text-pomegranate-400',
};
// `font-bold` (was `font-medium`) + a border for definition — same bolder
// treatment as RequestStatusBadge, minus its `uppercase` (stage text like
// "Closed Won" is copied verbatim from SFDC, not a fixed-width status word).
const OPP_PILL_CLASS = 'inline-flex items-center shrink-0 px-1.5 py-0.5 rounded-md border text-[10px] font-bold whitespace-nowrap';
const OPP_STAGE_NEUTRAL_TONE = 'bg-bg-secondary dark:bg-nightshade-800 border-primary/50 dark:border-nightshade-700 text-content-tertiary dark:text-nightshade-500';

/** One Opportunity row: collapsed summary + meta-chips, expanding into the
 * SFDC link → Key Personas → interleaved activity stream (Approach, U8). */
function OppRow({ accountId, opp }: { accountId: string; opp: OpportunityRow }) {
  const [expanded, setExpanded] = useState(false);
  const [subOpenKey, setSubOpenKey] = useState<string | null>(null);
  // Fetched on expand only (Decision 6) — `opportunityId: null` while
  // collapsed disables the query entirely.
  const { data, isLoading, error } = useFlowAccountOpportunityActivity(accountId, expanded ? opp.id : null);

  const setCount = personaSetCount(opp.personas);
  const closeStatus = oppCloseStatus(opp.stage, opp.is_closed);
  // "Closed Won"/"Closed Lost" stage text already unambiguously conveys
  // both closed-ness and win/loss — color the stage pill instead of also
  // rendering a separate status pill (avoids "Closed Won · Closed" double-
  // redundancy, Spencer's screenshot ask). Only an open stage (or the
  // generic-closed fallback above) gets its own status pill.
  const stageConveysStatus = stageConveysCloseStatus(closeStatus);
  const metaText = oppMetaText(opp);
  const stream = useMemo(
    () => (data ? buildOpportunityActivityStream(opp.next_step_history, data.calls, data.emails) : { items: [], overflow: false }),
    [data, opp.next_step_history],
  );

  return (
    <div className="border-b border-primary/40 dark:border-nightshade-800 last:border-b-0">
      <div
        role="button" tabIndex={0} aria-expanded={expanded} onClick={() => setExpanded(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
        className="w-full flex gap-2 py-2 text-left items-start hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded cursor-pointer"
      >
        {/* Chevron leads the row (left, first child) — matches the
            CallSubRow/EmailSubRow sub-row trigger convention (chevron before
            the icon tile). This row stays `items-start` (title + meta +
            chips stack to 3 lines, unlike the single-line sub-rows), so the
            chevron keeps its own `mt-0.5` to align against the first line of
            content instead of floating in the vertical middle of the whole
            block. */}
        {expanded
          ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
          : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
        {/* Icon tile — same slot/shape as the Timeline/Signals row uniform
            (AccountTimeline.tsx KIND_CONFIG rows: `w-[22px] h-[22px] ...
            rounded-md`), using the shared opportunity/deal icon+color. */}
        <span className={`w-[22px] h-[22px] shrink-0 rounded-md flex items-center justify-center ${OPPORTUNITY_TILE_CONFIG.tileClass}`}>
          <OPPORTUNITY_TILE_CONFIG.Icon className="w-3.5 h-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="flex items-center gap-1 min-w-0">
              {/* Stage/status pill(s) lead the title row, before the opp
                  name — Spencer's "should've followed... before the opp
                  name on the first row" correction to the earlier build,
                  which had these on the meta line below. */}
              {opp.stage && (
                <span
                  data-testid="opp-stage-pill"
                  className={`${OPP_PILL_CLASS} ${stageConveysStatus ? OPP_STATUS_TONE[closeStatus] : OPP_STAGE_NEUTRAL_TONE}`}
                >
                  Stage: {opp.stage}
                </span>
              )}
              {!stageConveysStatus && (
                <span data-testid="opp-status-pill" className={`${OPP_PILL_CLASS} ${OPP_STATUS_TONE[closeStatus]}`}>
                  {OPP_STATUS_LABEL[closeStatus]}
                </span>
              )}
              <span className="min-w-0 text-[12.5px] font-medium text-content-primary dark:text-nightshade-100 truncate">{opp.name}</span>
              <SfdcCloudIcon kind="Opportunity" id={opp.id} />
            </span>
            <span className="text-[12px] tabular-nums text-content-secondary dark:text-nightshade-400 shrink-0">{money(opp.amount) ?? '—'}</span>
          </div>
          <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 flex items-center gap-1.5 flex-wrap">
            {metaText && <span>{metaText}</span>}
          </div>
          {/* Meta-chips (Decision 5 — no "no notes" chip). */}
          <div className="flex gap-1 mt-1 flex-wrap">
            <MetaChip count={setCount} singular="persona set" plural="personas set" emptyLabel="no personas set" />
            <MetaChip count={opp.call_count} singular="call" plural="calls" emptyLabel="no calls" />
            <MetaChip count={opp.email_count} singular="email" plural="emails" emptyLabel="no emails" />
          </div>
        </span>
      </div>
      {expanded && (
        <div className="pl-6 pr-2 pb-3">
          <a
            href={sfdcRecordUrl('Opportunity', opp.id)} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11.5px] font-medium text-content-action dark:text-blueberry-400 bg-blueberry-50 dark:bg-blueberry-900/20 border border-blueberry-100 dark:border-blueberry-900/40 rounded-md px-2 py-1 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/30 hover:text-content-action-hover dark:hover:text-blueberry-300"
          >
            Open in Salesforce ↗
          </a>

          <div className="mt-3">
            <div className={OSEC_HEAD_CLASS}>
              Key Personas<span className={OSEC_COUNT_CLASS}>{setCount}/6 set</span>
            </div>
            {PERSONA_ROWS.map(r => (
              <PersonaRow
                key={r.key}
                label={r.label}
                persona={opp.personas[r.key]}
                isPrimaryClayUser={r.key === 'primary_clay_user'}
                primaryClayUserName={opp.primary_clay_user_name}
                primaryClayUserTitle={opp.primary_clay_user_title}
              />
            ))}
          </div>

          <div className="mt-3">
            <div className={OSEC_HEAD_CLASS}>Activity</div>
            {isLoading && <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 py-2">Loading activity…</div>}
            {error && <div className="text-[11px] text-content-danger dark:text-red-400 py-2">Activity failed to load.</div>}
            {!isLoading && !error && (
              <>
                {/* Decision 8 — every account call rendered here is date-windowed,
                    not deal-attributed; say so once rather than per-row noise. */}
                {data && data.calls.length > 0 && (
                  <div className="text-[10px] italic text-content-tertiary dark:text-nightshade-500 mt-1 mb-0.5">
                    {DEAL_WINDOW_NOTE}
                  </div>
                )}
                {stream.items.length === 0
                  ? <div className="text-[11px] italic text-content-tertiary dark:text-nightshade-500 py-2">No activity.</div>
                  : (
                    <div data-testid="opp-activity-stream">
                      {stream.items.map(item => {
                        if (item.kind === 'nsh') return <NshStreamRow key={item.key} when={item.when} text={item.text} />;
                        if (item.kind === 'call') {
                          return (
                            <CallSubRow
                              key={item.key} call={item.call}
                              isOpen={subOpenKey === item.key}
                              onToggle={() => setSubOpenKey(k => (k === item.key ? null : item.key))}
                            />
                          );
                        }
                        return (
                          <EmailSubRow
                            key={item.key} email={item.email}
                            isOpen={subOpenKey === item.key}
                            onToggle={() => setSubOpenKey(k => (k === item.key ? null : item.key))}
                          />
                        );
                      })}
                    </div>
                  )}
                {stream.overflow && (
                  <a
                    href={sfdcRecordUrl('Opportunity', opp.id)} target="_blank" rel="noreferrer"
                    className="mt-1.5 inline-block text-[11px] text-content-action dark:text-blueberry-400 hover:underline"
                  >
                    View more in Salesforce ↗
                  </a>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Opportunities panel (top-right quad, Opps tab). */
export function AccountOppsPanel({ accountId }: { accountId: string }) {
  const { data, isLoading, error } = useFlowAccountOpportunities(accountId);
  const muted = 'text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center';
  if (isLoading) return <div className={muted}>Loading…</div>;
  if (error) return <div className="text-[12px] text-content-danger dark:text-red-400 py-4 text-center">Opportunities failed to load.</div>;
  if (!data || data.opportunities.length === 0) return <div className={muted}>No opportunities.</div>;
  return (
    <div>
      {data.opportunities.map(o => (
        <OppRow key={o.id} accountId={accountId} opp={o} />
      ))}
    </div>
  );
}
